import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PaymentMethodForm } from "@/components/host/payment-methods/PaymentMethodForm";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { PaymentProvider } from "@/types/payment";

const AddPaymentMethodPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (data: {
    provider: PaymentProvider;
    phoneNumber: string;
    accountHolder?: string;
    accountType?: string;
    cryptoAddress?: string;
    paypalEmail?: string;
  }) => {
    setIsLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Create verification request
      const { error: verificationError } = await supabase
        .from("payment_detail_verifications")
        .insert({
          host_id: user.id,
          phone_number: data.provider === "paypal" ? data.paypalEmail : data.phoneNumber,
          provider: data.provider,
          mtn_account_holder: data.accountHolder,
          mtn_account_type: data.accountType,
        });

      if (verificationError) throw verificationError;

      // Update profile payment details
      const paymentDetails = {
        mobile_money: {
          provider: data.provider,
          phone_number: data.phoneNumber,
          verified: false,
        },
        crypto: data.cryptoAddress ? {
          address: data.cryptoAddress,
          verified: false,
        } : undefined,
        paypal: data.paypalEmail ? {
          email: data.paypalEmail,
          verified: false,
        } : undefined,
      };

      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          payment_details: paymentDetails,
        })
        .eq("id", user.id);

      if (profileError) throw profileError;

      toast({
        title: "Payment method added",
        description: "Your payment method has been submitted for verification.",
      });
      
      navigate("/host/settings");
    } catch (error) {
      console.error("Error adding payment method:", error);
      toast({
        title: "Error",
        description: "Failed to add payment method. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Add Payment Method</h1>
          <button
            onClick={() => navigate("/host/settings")}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        </div>
        
        <PaymentMethodForm 
          onSubmit={handleSubmit}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default AddPaymentMethodPage;